import { convertMomentToDateFnsFormat, parseTimeStamp } from '@/lib/parseTimeStamp';
import { DateFormat } from '@heidi/schema/ml-scribe';
import { addDays, format, isSameDay, isValid, subDays } from 'date-fns';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

type TimeFormatConfig = {
  showYear: boolean;
  includeTime: boolean;
  contextual?: boolean;
  defaultDateFormat: DateFormat;
};

type FormatStyle = 'default' | 'short' | 'contextual' | 'dateOnly' | 'timeOnly';

/**
 * Helper function to convert DateFormat to non-year format
 * @param format The DateFormat enum value (which contains moment format string)
 */
function getShortDateFormat(format: DateFormat): string {
  const formatMap: Record<DateFormat, string> = {
    [DateFormat.DAY_MONTH_YEAR]: 'dd/MM',
    [DateFormat.DAY_MONTH_YEAR_ABBR]: 'd MMM',
    [DateFormat.DAY_MONTH_YEAR_DOT]: 'dd.MM',
    [DateFormat.DAY_MONTH_YEAR_DOT_SHORT]: 'dd.MM',
    [DateFormat.MONTH_DAY_YEAR]: 'MM/dd',
    [DateFormat.MONTH_DAY_YEAR_SHORT]: 'MM/dd',
    [DateFormat.MONTH_FULL_DAY_YEAR]: 'MMMM dd',
    [DateFormat.MONTH_ABBR_DAY_YEAR]: 'MMM dd',
    [DateFormat.YEAR_MONTH_DAY]: 'MM/dd', // fallback to MM/dd when original is year-first
  };

  return formatMap[format];
}

export function useFormatSessionTime(isUtc = true) {
  const intl = useIntl();

  const formatTimeOfDay = useCallback(
    (localTime: Date) => {
      const options: Intl.DateTimeFormatOptions = {
        hour: '2-digit',
        minute: '2-digit',
      };

      if (intl.locale.toLowerCase().startsWith('ar')) {
        options.numberingSystem = 'latn';
      }

      return intl.formatTime(localTime, options);
    },
    [intl]
  );

  const formatConfigs: Record<FormatStyle, TimeFormatConfig> = {
    default: {
      showYear: true,
      includeTime: true,
      contextual: true,
      defaultDateFormat: DateFormat.DAY_MONTH_YEAR,
    },
    short: {
      showYear: false,
      includeTime: true,
      contextual: true,
      defaultDateFormat: DateFormat.DAY_MONTH_YEAR,
    },
    contextual: {
      showYear: false,
      includeTime: true,
      contextual: true,
      defaultDateFormat: DateFormat.DAY_MONTH_YEAR,
    },
    dateOnly: {
      showYear: true,
      includeTime: false,
      contextual: false,
      defaultDateFormat: DateFormat.DAY_MONTH_YEAR,
    },
    timeOnly: {
      showYear: false,
      includeTime: true,
      contextual: false,
      defaultDateFormat: DateFormat.DAY_MONTH_YEAR,
    },
  };

  const getContextualFormat = useCallback(
    (localTime: Date) => {
      const today = new Date();
      const yesterday = subDays(today, 1);
      const tomorrow = addDays(today, 1);
      const time = formatTimeOfDay(localTime);

      if (isSameDay(localTime, today)) {
        return intl.formatMessage(
          {
            defaultMessage: 'Today {time}',
            id: 'VA4lYYu3wE',
            description: 'Session start time shown when the session is today',
          },
          { time }
        );
      }
      if (isSameDay(localTime, yesterday)) {
        return intl.formatMessage(
          {
            defaultMessage: 'Yesterday {time}',
            id: 'MmorSeaMg1',
            description: 'Session start time shown when the session was yesterday',
          },
          { time }
        );
      }
      if (isSameDay(localTime, tomorrow)) {
        return intl.formatMessage(
          {
            defaultMessage: 'Tomorrow {time}',
            id: 'H8mh0nElpG',
            description: 'Session start time shown when the session is tomorrow',
          },
          { time }
        );
      }
      return null;
    },
    [formatTimeOfDay, intl]
  );

  return (
    time: string,
    customFormat?: DateFormat,
    style: FormatStyle = 'default',
    overrides: Partial<TimeFormatConfig> = {}
  ) => {
    if (!time) {
      return intl.formatMessage({
        defaultMessage: 'Not started',
        id: 'okyIzERL51',
        description: 'Fallback label when a session has no start time',
      });
    }

    const config = {
      ...formatConfigs[style],
      ...overrides,
    };

    const localTime = parseTimeStamp(time, isUtc);

    // Guard against invalid dates to prevent RangeError
    if (!isValid(localTime)) {
      return intl.formatMessage({
        defaultMessage: 'Not started',
        id: 'okyIzERL51',
        description: 'Fallback label when a session has no start time',
      });
    }

    // Check for contextual format first if enabled
    if (config.contextual) {
      const contextualResult = getContextualFormat(localTime);
      if (contextualResult) return contextualResult;
    }

    // Handle date format
    const dateFormat = customFormat ?? config.defaultDateFormat;
    const dateFormatString = config.showYear
      ? convertMomentToDateFnsFormat(dateFormat)
      : getShortDateFormat(dateFormat);

    let result = format(localTime, dateFormatString);

    // Append time if needed
    if (config.includeTime) {
      const timeStr = formatTimeOfDay(localTime);
      result = `${result} ${timeStr}`;
    }

    return result.trim();
  };
}

// Example usage:
/*
const formatSessionTime = useFormatSessionTime();

// Default usage (with year and time)
formatSessionTime(someTime);  // "25/12/2023 02:30 PM"

// Short format (no year)
formatSessionTime(someTime, undefined, 'short');  // "25/12 02:30 PM"

// With custom format
formatSessionTime(someTime, DateFormat.DAY_MONTH_YEAR_ABBR);  // "25 Dec 2023 02:30 PM"
formatSessionTime(someTime, DateFormat.DAY_MONTH_YEAR_ABBR, 'short');  // "25 Dec 02:30 PM"

// Date only
formatSessionTime(someTime, DateFormat.MONTH_FULL_DAY_YEAR, 'dateOnly');  // "December 25 2023"

// Time only
formatSessionTime(someTime, undefined, 'timeOnly');  // "02:30 PM"
*/
